import {
  CheckCircle,
  DoNotDisturb,
  Edit,
  MoreVert,
  NoAccounts,
  Pending,
} from "@mui/icons-material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Container,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { List } from "../@types/list";
import EmptyState from "../SharedComponents/EmptyState/EmptyState";
import { Layout } from "../SharedComponents/Layout";
import { Pagination } from "../SharedComponents/Pagination";
import { SearchInput } from "../SharedComponents/SearchInput";
import FilterMenu from "../SharedComponents/FilterManager/FilterMenu";
import FilterChips from "../SharedComponents/FilterManager/FilterChips";
import { FilterOption, ActiveFilter } from "../SharedComponents/FilterManager/types";
import { environment } from "../utils/environment";
import { calculateTotalPages } from "../utils/pagination";
import { CrpInputField, validateCpr } from "./components/CrpInputField";

const formatLocalDateTime = (isoString?: string | null) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

type PsychologistData = {
  user_id: number;
  user_uid: string;
  email: string;
  first_name: string;
  last_name: string;
  cpf: string;
  crp: string | null;
  verified: boolean;
  promoter_name: string | null;
  created_at: string | null;
};

type OrderField = "created_at" | "name";
type OrderDirection = "ASC" | "DESC";

export default function PsychologistManagementPage() {
  const [data, setData] = useState<List<PsychologistData>>({
    data: [],
    page: 1,
    limit: 10,
    total: 0,
    filter: "",
  });
  const [order, setOrder] = useState<{
    field: OrderField;
    direction: OrderDirection;
  }>({
    field: "created_at",
    direction: "DESC",
  });
  const [loading, setLoading] = useState(true);

  const [openCrpModal, setOpenCrpModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedPsy, setSelectedPsy] = useState<PsychologistData | null>(null);

  const [toEdit, setToEdit] = useState<PsychologistData | null>(null);
  const [editForm, setEditForm] = useState<{ nome: string; crp: string }>({
    nome: "",
    crp: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuPsy, setMenuPsy] = useState<PsychologistData | null>(null);
  const menuOpen = Boolean(anchorEl);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const [crpApprove, setCrpApprove] = useState("");
  const isCrpApproveValid =
    crpApprove.trim() !== "" && validateCpr(crpApprove).isValid;
  const isCrpEditValid =
    editForm.crp.trim() !== "" && validateCpr(editForm.crp).isValid;

  // Filtros extras (status e crp)
  const [extraFilters, setExtraFilters] = useState<ActiveFilter[]>([]);

  // Mapeamento de labels para os valores true/false por campo
  const availableFilters: FilterOption[] = [
    {
      key: "status",
      label: "Status",
      values: [
        { label: "Ativo", value: true },
        { label: "Inativo", value: false },
      ],
    },
    {
      key: "crp",
      label: "CRP",
      values: [
        { label: "Sem CRP", value: true },
        { label: "Com CRP", value: false },
      ],
    },
  ];

  const fetchPsychologists = useCallback(
    async (page = 1, filterText = data.filter) => {
      setLoading(true);
      try {
        const orderParam =
          order.field === "created_at"
            ? { created_at: order.direction }
            : { name: order.direction };
        const resp = await axios.get<List<PsychologistData>>(
          `${environment.apiBaseUrl}api/v1/admin/psychologists/search`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("AccessToken")}`,
            },
            params: {
              page,
              limit: data.limit,
              name: filterText || undefined,
              verified: extraFilters.find((f) => f.key === "status")?.value,
              crpNotFilled: extraFilters.find((f) => f.key === "crp")?.value,
              order: orderParam,
            },
          }
        );
        setData({
          data: resp.data?.data || [],
          page: resp.data?.page || 1,
          limit: resp.data?.limit || 10,
          total: resp.data?.total || 0,
          filter: filterText,
        });
      } catch {
        setSnackbar({
          open: true,
          message: "Erro ao buscar psicólogos.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }, [data.limit, extraFilters, order]);

  useEffect(() => {
    fetchPsychologists();
  }, [fetchPsychologists]);

  const totalPages = useMemo(
    () => calculateTotalPages(data.total, data.limit),
    [data.limit, data.total]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const targetPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
      if (targetPage === data.page) {
        return;
      }
      fetchPsychologists(targetPage, data.filter);
    },
    [data.filter, data.page, fetchPsychologists, totalPages]
  );

  const handleSort = (field: OrderField) => {
    setOrder((prev) => {
      if (prev.field === field) {
        return {
          ...prev,
          direction: prev.direction === "ASC" ? "DESC" : "ASC",
        };
      }

      return {
        field,
        direction: field === "created_at" ? "DESC" : "ASC",
      };
    });
  };

  const doPatchStatus = async (
    userId: number,
    crp: string | null,
    verified: boolean
  ) => {
    try {
      await axios.patch(
        `${environment.apiBaseUrl}api/v1/admin/validate-psychologists`,
        {
          data: [{ userId, crp, verified }],
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("AccessToken")}`,
          },
        }
      );
      setSnackbar({
        open: true,
        message: `Psicólogo ${verified ? "ativado" : "inativado"} com sucesso!`,
        severity: "success",
      });
      fetchPsychologists(data.page, data.filter);
    } catch {
      setSnackbar({
        open: true,
        message: `Erro ao ${verified ? "ativar" : "inativar"} psicólogo.`,
        severity: "error",
      });
    }
  };

  const handleChangeStatus = (psy: PsychologistData, newStatus: boolean) => {
    closeMenu();
    setSelectedPsy(psy);
    if (newStatus) {
      setCrpApprove(psy.crp ?? "");
      setOpenCrpModal(true);
    } else {
      setOpenConfirmModal(true);
    }
  };

  const handleActivateWithCrp = async () => {
    if (!selectedPsy) return;
    if (!isCrpApproveValid) {
      setSnackbar({
        open: true,
        message: "Por favor, insira um CRP válido.",
        severity: "error",
      });
      return;
    }
    await doPatchStatus(
      selectedPsy.user_id,
      crpApprove.trim() === "" ? null : crpApprove.trim(),
      true
    );
    setOpenCrpModal(false);
    setSelectedPsy(null);
    setCrpApprove("");
  };

  const handleEditSave = async () => {
    if (!toEdit || !isCrpEditValid) return;
    setEditLoading(true);
    try {
      await axios.patch(
        `${environment.apiBaseUrl}api/v1/admin/validate-psychologists`,
        {
          data: [
            {
              userId: toEdit.user_id,
              nome: editForm.nome || null,
              crp: editForm.crp || null,
              verified: toEdit.verified,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("AccessToken")}`,
          },
        }
      );
      setSnackbar({
        open: true,
        message: "Psicólogo atualizado com sucesso!",
        severity: "success",
      });
      setToEdit(null);
      fetchPsychologists(data.page, data.filter);
    } catch {
      setSnackbar({
        open: true,
        message: "Erro ao atualizar psicólogo.",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const openMenu = (
    e: React.MouseEvent<HTMLElement>,
    psy: PsychologistData
  ) => {
    setAnchorEl(e.currentTarget);
    setMenuPsy(psy);
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setMenuPsy(null);
  };
  const openEditDialog = (psy: PsychologistData) => {
    setToEdit(psy);
    setEditForm({
      nome: `${psy.first_name} ${psy.last_name}`,
      crp: psy.crp || "",
    });
    closeMenu();
  };

  return (
    <Layout>
      <Container
        maxWidth="lg"
        style={{
          padding: "80px 20px",
        }}
      >
        <Stack direction="column" spacing={1.25} mb={2}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h2">Psicólogos</Typography>
            <Chip
              label={`${data?.total ?? 0} ${
                data?.total !== 1 ? "encontrados(as)" : "encontrado(a)"
              }`}
              size="small"
            />
          </Box>

          <Typography variant="subtitle1" color="text.secondary">
            Gerencie os psicólogos cadastrados
          </Typography>
        </Stack>

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              gap: {
                xs: '1rem',
              },
              alignItems: {
                sm: 'center'
              },
              justifyContent: {
                xs: 'end',

              },
              flexWrap: 'wrap'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'start',
                  md: 'end'
                },
                flexWrap: 'wrap'
              }}
            >
              <SearchInput
                label="Pesquisar por nome, email, CPF..."
                onChangeFilter={(filter) => setData((d) => ({ ...d, filter }))}
                onSearch={(filter) => fetchPsychologists(1, filter)}
              />

              <FilterMenu
                availableFilters={availableFilters}
                extraFilters={extraFilters}
                onChange={setExtraFilters}
              />
            </Box>
          </Box>

          <FilterChips
            availableFilters={availableFilters}
            extraFilters={extraFilters}
            onChange={setExtraFilters}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={
                    order.field === "name"
                      ? (order.direction.toLowerCase() as "asc" | "desc")
                      : false
                  }
                >
                  <TableSortLabel
                    active={order.field === "name"}
                    direction={
                      order.field === "name"
                        ? (order.direction === "ASC" ? "asc" : "desc")
                        : "asc"
                    }
                    onClick={() => handleSort("name")}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>CRP</TableCell>
                <TableCell>Solicitado Por</TableCell>
                <TableCell
                  sortDirection={
                    order.field === "created_at"
                      ? (order.direction.toLowerCase() as "asc" | "desc")
                      : false
                  }
                >
                  <TableSortLabel
                    active={order.field === "created_at"}
                    direction={
                      order.field === "created_at"
                        ? (order.direction === "ASC" ? "asc" : "desc")
                        : "desc"
                    }
                    onClick={() => handleSort("created_at")}
                  >
                    Data de Cadastro
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <EmptyState
                      icon={NoAccounts}
                      title="Nenhum psicólogo encontrado"
                      description="Tente ajustar os filtros ou adicionar novos psicólogos."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                data.data.map((psy) => (
                  <TableRow key={psy.user_id} hover>
                    <TableCell>
                      {`${psy.first_name} ${psy.last_name}`} 
                      <br/>
                      <Typography variant="caption" color="text.secondary">
                        {psy.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{psy.cpf}</TableCell>
                    <TableCell sx={{wordBreak: 'break-all'}}>{psy.crp || "—"}</TableCell>
                    <TableCell>
                      {psy.promoter_name || "Cadastro Próprio"}
                    </TableCell>
                    <TableCell>{formatLocalDateTime(psy.created_at)}</TableCell>
                    <TableCell align="center">
                      {psy.verified ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Ativo"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Pending />}
                          label="Inativo"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="actions"
                        size="medium"
                        sx={{
                          backgroundColor: "grey.200",
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "grey.300",
                          },
                        }}
                        onClick={(e) => openMenu(e, psy)}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={menuOpen && menuPsy?.user_id === psy.user_id}
                        onClose={closeMenu}
                      >
                        <MenuItem onClick={() => openEditDialog(psy)}>
                          <Edit fontSize="small" sx={{ mr: 1 }} /> Editar
                        </MenuItem>
                        {psy.verified ? (
                          <MenuItem
                            onClick={() => handleChangeStatus(psy, false)}
                          >
                            <DoNotDisturb fontSize="small" sx={{ mr: 1 }} />{" "}
                            Inativar
                          </MenuItem>
                        ) : (
                          <MenuItem
                            onClick={() => handleChangeStatus(psy, true)}
                          >
                            <CheckCircle fontSize="small" sx={{ mr: 1 }} />{" "}
                            Ativar
                          </MenuItem>
                        )}
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          currentPage={data.page}
          totalPages={totalPages}
          onChangePage={handlePageChange}
        />

        <Dialog
          open={openCrpModal}
          onClose={() => {
            setOpenCrpModal(false);
            setSelectedPsy(null);
            setCrpApprove("");
          }}
          variant="modal"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle variant="modal">
            <VerifiedUserIcon />
            Ativar {selectedPsy?.first_name} {selectedPsy?.last_name}
          </DialogTitle>
          <DialogContent variant="modal">
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography color="text.secondary">
                O CRP é obrigatório para ativação do vínculo de psicólogo(a). Informe-o abaixo para liberar o acesso.
              </Typography>
              <CrpInputField
                label="CRP"
                value={crpApprove}
                onChange={setCrpApprove}
                required={true}
              />
            </Box>
          </DialogContent>
          <DialogActions variant="modal" sx={{ justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setOpenCrpModal(false);
                setSelectedPsy(null);
                setCrpApprove("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!isCrpApproveValid}
              onClick={handleActivateWithCrp}
            >
              Ativar
            </Button>
          </DialogActions>
        </Dialog>

        <Modal
          open={openConfirmModal}
          onClose={() => {
            setOpenConfirmModal(false);
            setSelectedPsy(null);
          }}
        >
          <Box
            sx={{
              position: "absolute" as const,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 1,
              p: 3,
            }}
          >
            <Typography variant="h6" mb={2}>
              Confirmar Inativação
            </Typography>
            <Typography mb={2}>
              Tem certeza que deseja inativar{" "}
              <strong>
                {selectedPsy?.first_name} {selectedPsy?.last_name}
              </strong>
              ?
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta ação irá remover o acesso a funcionalidades de psicólogo.
            </Alert>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setOpenConfirmModal(false);
                  setSelectedPsy(null);
                }}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  if (selectedPsy) {
                    doPatchStatus(selectedPsy.user_id, selectedPsy.crp, false);
                  }
                  setOpenConfirmModal(false);
                  setSelectedPsy(null);
                }}
              >
                Inativar
              </Button>
            </Box>
          </Box>
        </Modal>

        <Dialog open={!!toEdit} onClose={() => setToEdit(null)} variant="modal">
          <DialogTitle variant="modal">
            <EditNoteIcon />
            Editar {toEdit?.first_name} {toEdit?.last_name}
          </DialogTitle>
          <DialogContent variant="modal">
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Nome Completo"
                value={editForm.nome}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, nome: e.target.value }))
                }
                fullWidth
              />
              <CrpInputField
                label="CRP"
                value={editForm.crp}
                onChange={(v) => setEditForm((f) => ({ ...f, crp: v }))}
                required={true}
              />
            </Box>
          </DialogContent>
          <DialogActions variant="modal" sx={{ justifyContent: "end" }}>
            <Button color="error" variant="outlined" onClick={() => setToEdit(null)}>Cancelar</Button>

            <Button
              variant="contained"
              onClick={handleEditSave}
              disabled={editLoading || !isCrpEditValid || editForm.nome === ""}
            >
              {editLoading ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
}
